import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { statusLabel } from './materialHuntStatus';
import css from './MaterialHuntPage.module.css';

const SKELETON_TILES = 8;

/**
 * /material-hunt — the Material Hunt index: "What material is this?" hero
 * with the start-a-hunt CTA, then the grid of open + solved hunts. Fully
 * public: the cards render from the server's snapshot JSON (no Marketplace
 * API involved), so anonymous visitors arriving from Threads/WhatsApp see
 * the same page a logged-in designer does.
 */
export const MaterialHuntPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled, hunts, fetchInProgress, fetchError, source } = props;

  const title = intl.formatMessage({
    id: 'MaterialHuntPage.title',
    defaultMessage: 'Material Hunt — what material is this?',
  });
  const description = intl.formatMessage({
    id: 'MaterialHuntPage.description',
    defaultMessage:
      'Upload a photo of an unknown material. Matterax AI analyses it, searches the catalogue, and the material community helps identify it.',
  });

  const showSkeletons = fetchInProgress || (!fetchError && source === null);
  const hasHunts = !showSkeletons && hunts.length > 0;

  return (
    <Page
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="MaterialHuntPage" />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          <div className={css.hero}>
            <H3 as="h1" className={css.heroTitle}>
              <FormattedMessage
                id="MaterialHuntPage.heroTitle"
                defaultMessage="What material is this?"
              />
            </H3>
            <p className={css.heroText}>
              <FormattedMessage
                id="MaterialHuntPage.heroText"
                defaultMessage="Upload a material, project detail or screenshot. Matterax will analyse it, search for similar products — and if we can't identify it, the material community takes over."
              />
            </p>
            <NamedLink name="MaterialHuntNewPage" className={css.heroCta}>
              <FormattedMessage id="MaterialHuntPage.heroCta" defaultMessage="Upload an image" />
            </NamedLink>
          </div>

          {fetchError ? (
            <p className={css.error}>
              <FormattedMessage
                id="MaterialHuntPage.error"
                defaultMessage="Oops, something went wrong. Please refresh the page."
              />
            </p>
          ) : null}

          <h2 className={css.sectionTitle}>
            <FormattedMessage id="MaterialHuntPage.recentHunts" defaultMessage="Recent hunts" />
          </h2>

          {showSkeletons ? (
            <div className={css.grid}>
              {[...Array(SKELETON_TILES)].map((_, i) => (
                <div key={`skeleton-${i}`} className={css.skeletonItem}>
                  <div className={css.skeletonImage} />
                  <div className={css.skeletonLine} />
                </div>
              ))}
            </div>
          ) : hasHunts ? (
            <div className={css.grid}>
              {hunts.map(hunt => (
                <NamedLink
                  key={hunt.slug}
                  className={css.card}
                  name="MaterialHuntRequestPage"
                  params={{ slug: hunt.slug }}
                >
                  <div className={css.cardImageWrapper}>
                    <img
                      className={css.cardImage}
                      src={hunt.thumbUrl}
                      alt={hunt.title || 'Material hunt'}
                      loading="lazy"
                    />
                    <span
                      className={
                        hunt.status === 'confirmed' ? css.statusBadgeConfirmed : css.statusBadge
                      }
                    >
                      {statusLabel(hunt.status)}
                    </span>
                  </div>
                  <div className={css.cardBody}>
                    <span className={css.cardTitle}>{hunt.title}</span>
                    <span className={css.cardMeta}>
                      <FormattedMessage
                        id="MaterialHuntPage.cardMeta"
                        defaultMessage="{answers, plural, =0 {No answers yet} one {# answer} other {# answers}} · {looking, plural, =0 {} one {# looking} other {# looking}}"
                        values={{
                          answers: hunt.suggestionCount || 0,
                          looking: hunt.interestCount || 0,
                        }}
                      />
                    </span>
                  </div>
                </NamedLink>
              ))}
            </div>
          ) : (
            <div className={css.emptyState}>
              <p className={css.emptyDescription}>
                <FormattedMessage
                  id="MaterialHuntPage.noHunts"
                  defaultMessage="No hunts yet — be the first to ask the community."
                />
              </p>
            </div>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { hunts, total, fetchInProgress, fetchError, source } = state.MaterialHuntPage;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    hunts,
    total,
    fetchInProgress,
    fetchError,
    source,
  };
};

const MaterialHuntPage = compose(connect(mapStateToProps))(MaterialHuntPageComponent);

export default MaterialHuntPage;
